import { XMarkIcon } from "@heroicons/react/24/outline";
import { router } from "@inertiajs/react";
import type { Audit } from "@/types";

type ShareAuditDialogProps = {
  audit: Audit;
  open: boolean;
  onClose: () => void;
};

export default function ShareAuditDialog({ audit, open, onClose }: ShareAuditDialogProps) {
  if (!open) {
    return null;
  }

  const updateSharing = (isPublic: boolean) => {
    router.patch(
      `/audits/${audit.id}/sharing`,
      { public: isPublic },
      {
        preserveScroll: true,
        onSuccess: onClose,
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close sharing dialog"
        className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{audit.public ? "Manage sharing" : "Share audit"}</h2>
            <p className="mt-2 text-sm text-gray-600">
              {audit.public
                ? "This audit is currently public. Anyone with this link can view it without signing in."
                : "Anyone with this link can view this audit without signing in."}
            </p>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Close">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {audit.public ? (
          <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Public link</p>
            <a href={audit.public_url} className="break-all text-sm font-medium text-primary hover:text-primary/80">
              {audit.public_url}
            </a>
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          {audit.public ? (
            <>
              <a href={audit.public_url} className="btn" target="_blank" rel="noreferrer">
                Open public audit
              </a>
              <button type="button" className="btn btn-danger" onClick={() => updateSharing(false)}>
                Make private
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => updateSharing(true)}>
              Make public
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
